/**
 * Tenant Onboarding Wizard — Ops Portal
 * 5-Step: Receive Devices → Register Customer → Assign Operator → Activate Passports → Dispatch Shipment
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  Package, Building2, UserCog, ShieldCheck, Truck, CheckCircle, ArrowRight,
  Loader2, ChevronLeft, Plus
} from 'lucide-react';
import { getBearerToken } from '@/context/TrackOticAuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function OpsOnboardingWizardPage() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // Step 1
  const [inbound, setInbound] = useState([]);
  const [selectedInbound, setSelectedInbound] = useState([]);
  const [receiveResult, setReceiveResult] = useState(null);

  // Step 2
  const [custForm, setCustForm] = useState({
    name: '', customer_type: 'STORE', address: '', city: '', state: '',
    pin_code: '', contact_phone: '', contact_email: ''
  });
  const [createdCustomer, setCreatedCustomer] = useState(null);
  const [existingCustomers, setExistingCustomers] = useState([]);
  const [useExistingCustomer, setUseExistingCustomer] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');

  // Step 3
  const [inventoryDevices, setInventoryDevices] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [selectedDeviceRid, setSelectedDeviceRid] = useState('');
  const [selectedOperatorId, setSelectedOperatorId] = useState('');
  const [assignResult, setAssignResult] = useState(null);

  // Step 4
  const [batches, setBatches] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [activateForm, setActivateForm] = useState({
    quantity: 1, invoice_number: '', product_description: '', customer_name: ''
  });
  const [activatedPassports, setActivatedPassports] = useState([]);

  // Step 5
  const [shipForm, setShipForm] = useState({ temp_min: 2, temp_max: 8, notes: '' });
  const [dispatchResult, setDispatchResult] = useState(null);

  const token = getBearerToken();
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    const token = getBearerToken();
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [inb, cust, inv, drv, bat] = await Promise.all([
        axios.get(`${API_BASE}/api/v1/ops/onboarding/inbound-devices`, { headers }),
        axios.get(`${API_BASE}/api/v1/ops/onboarding/customers`, { headers }),
        axios.get(`${API_BASE}/api/v1/ops/onboarding/inventory-devices`, { headers }),
        axios.get(`${API_BASE}/api/v1/ops/onboarding/drivers`, { headers }),
        axios.get(`${API_BASE}/api/v1/ops/onboarding/passport-batches`, { headers }),
      ]);
      setInbound(inb.data || []);
      setExistingCustomers(cust.data || []);
      setInventoryDevices(inv.data || []);
      setDrivers(drv.data || []);
      setBatches(bat.data || []);
    } catch (e) {
      console.error('[OpsOnboarding] fetchAll failed:', e?.message || e);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Step 1: Receive
  const handleReceive = async () => {
    if (selectedInbound.length === 0) { toast.error('Select at least one device'); return; }
    try {
      setLoading(true);
      const res = await axios.post(`${API_BASE}/api/v1/ops/onboarding/step1-receive`,
        { registry_ids: selectedInbound }, { headers });
      setReceiveResult(res.data);
      toast.success(res.data.message);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  // Step 2: Register customer
  const handleRegisterCustomer = async () => {
    if (!custForm.name.trim()) { toast.error('Customer name required'); return; }
    try {
      setLoading(true);
      const res = await axios.post(`${API_BASE}/api/v1/ops/onboarding/step2-register-customer`,
        custForm, { headers });
      setCreatedCustomer(res.data);
      setSelectedCustomerId(res.data.customer_id);
      toast.success(res.data.message);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  // Step 3: Assign operator
  const handleAssignOperator = async () => {
    if (!selectedDeviceRid || !selectedOperatorId) {
      toast.error('Select device and operator'); return;
    }
    try {
      setLoading(true);
      const res = await axios.post(`${API_BASE}/api/v1/ops/onboarding/step3-assign-operator`,
        { registry_id: selectedDeviceRid, operator_user_id: selectedOperatorId }, { headers });
      setAssignResult(res.data);
      toast.success(res.data.message);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  // Step 4: Activate passports
  const handleActivatePassports = async () => {
    if (!selectedBatchId) { toast.error('Select a Passport QR book'); return; }
    try {
      setLoading(true);
      const res = await axios.post(`${API_BASE}/api/v1/ops/onboarding/step4-activate-passports`, {
        batch_id: selectedBatchId,
        quantity: parseInt(activateForm.quantity) || 1,
        invoice_number: activateForm.invoice_number || null,
        product_description: activateForm.product_description || null,
        customer_name: activateForm.customer_name || (createdCustomer?.name) || (existingCustomers.find(c => c.id === selectedCustomerId)?.name)
      }, { headers });
      setActivatedPassports(res.data.passports || []);
      toast.success(res.data.message);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  // Step 5: Dispatch
  const handleDispatch = async () => {
    const customerId = useExistingCustomer ? selectedCustomerId : createdCustomer?.customer_id;
    if (!customerId) { toast.error('Customer required'); return; }
    try {
      setLoading(true);
      const res = await axios.post(`${API_BASE}/api/v1/ops/onboarding/step5-dispatch`, {
        customer_location_id: customerId,
        device_registry_id: selectedDeviceRid || null,
        operator_user_id: selectedOperatorId || null,
        passport_ids: activatedPassports.map(p => p.passport_id),
        temp_min: parseFloat(shipForm.temp_min) || null,
        temp_max: parseFloat(shipForm.temp_max) || null,
        notes: shipForm.notes || null
      }, { headers });
      setDispatchResult(res.data);
      toast.success(res.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  const customerName = () => {
    if (createdCustomer) return createdCustomer.name;
    return existingCustomers.find(c => c.id === selectedCustomerId)?.name || 'Customer';
  };

  const steps = [
    { num: 1, icon: Package, label: 'Receive Devices' },
    { num: 2, icon: Building2, label: 'Register Customer' },
    { num: 3, icon: UserCog, label: 'Assign Operator' },
    { num: 4, icon: ShieldCheck, label: 'Activate Passports' },
    { num: 5, icon: Truck, label: 'Dispatch' }
  ];

  const FormField = ({ label, required, children }) => (
    <div>
      <label className="text-sm text-slate-600 dark:text-slate-400 mb-1 block">{label}{required && <span className="text-red-500">*</span>}</label>
      {children}
    </div>
  );

  const inputCls = "bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500";

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-4xl mx-auto" data-testid="ops-onboarding-wizard">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Customer Onboarding Wizard</h1>
        <p className="text-slate-600 dark:text-slate-400 text-sm">Receive → Register Customer → Assign Operator → Activate Passport QRs → Dispatch</p>
      </div>

      {/* Step Navigator */}
      <div className="flex items-center gap-1 flex-wrap">
        {steps.map((s, idx) => {
          const Icon = s.icon;
          const isActive = step === s.num;
          const isDone = step > s.num;
          return (
            <div key={s.num} className="flex items-center gap-1">
              {idx > 0 && <ArrowRight className={cn("w-3 h-3", isDone ? "text-teal-500" : "text-slate-600")} />}
              <button
                onClick={() => setStep(s.num)}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all",
                  isActive ? "bg-teal-600 text-white" :
                  isDone ? "bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 hover:bg-teal-200 dark:hover:bg-teal-900/50" :
                  "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                )}
                data-testid={`ops-step-${s.num}-btn`}
              >
                {isDone ? <CheckCircle className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                <span className="hidden sm:inline">{s.label}</span>
              </button>
            </div>
          );
        })}
      </div>

      {/* Step 1: Receive Devices */}
      {step === 1 && (
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="text-slate-900 dark:text-white flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-500" />Step 1 — Receive Devices from Track0tic
            </CardTitle>
          </CardHeader>
          <CardContent>
            {receiveResult ? (
              <div className="space-y-4">
                <div className="bg-emerald-50 dark:bg-green-900/20 border border-emerald-200 dark:border-green-700 rounded-lg p-4 text-center">
                  <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
                  <p className="text-green-700 dark:text-green-700 dark:text-green-400 font-semibold">{receiveResult.message}</p>
                </div>
                <Button onClick={() => setStep(2)} className="bg-teal-600 hover:bg-teal-700 w-full h-11">
                  Next: Register Customer <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            ) : inbound.length === 0 ? (
              <div className="space-y-4">
                <p className="text-amber-600 dark:text-amber-400 text-sm">No inbound devices awaiting acknowledgement. Devices will appear here once Track0tic dispatches them to your tenant.</p>
                <Button onClick={() => setStep(2)} variant="outline" className="border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300">
                  Skip to Step 2 <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-slate-700 dark:text-slate-300 text-sm">Select devices to acknowledge as received:</p>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {inbound.map(d => {
                    const checked = selectedInbound.includes(d.id);
                    return (
                      <label key={d.id}
                        className={cn("flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                          checked ? "bg-teal-50 dark:bg-teal-900/20 border-teal-300 dark:border-teal-700" : "bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600")}>
                        <input type="checkbox" checked={checked}
                          onChange={e => setSelectedInbound(prev => e.target.checked ? [...prev, d.id] : prev.filter(x => x !== d.id))}
                          data-testid={`inbound-cb-${d.device_uid}`} className="w-4 h-4" />
                        <div className="flex-1">
                          <p className="text-slate-900 dark:text-white font-mono text-sm">{d.device_uid}</p>
                          <p className="text-slate-600 dark:text-slate-400 text-xs">IMEI: {d.imei}</p>
                        </div>
                        <Badge className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">{d.device_type}</Badge>
                      </label>
                    );
                  })}
                </div>
                <Button onClick={handleReceive} disabled={loading || selectedInbound.length === 0}
                  className="bg-blue-600 hover:bg-blue-700 w-full h-11" data-testid="receive-devices-btn">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Package className="w-4 h-4 mr-2" />}
                  Receive {selectedInbound.length} Device{selectedInbound.length === 1 ? '' : 's'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 2: Register Customer */}
      {step === 2 && (
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="text-slate-900 dark:text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-teal-500" />Step 2 — Register Customer / Delivery Destination
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 mb-5">
              <Button variant={!useExistingCustomer ? "default" : "outline"} size="sm"
                onClick={() => setUseExistingCustomer(false)}
                className={!useExistingCustomer ? "bg-teal-600 text-white" : "border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300"}>
                New Customer
              </Button>
              <Button variant={useExistingCustomer ? "default" : "outline"} size="sm"
                onClick={() => setUseExistingCustomer(true)}
                className={useExistingCustomer ? "bg-teal-600 text-white" : "border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300"}>
                Existing ({existingCustomers.length})
              </Button>
            </div>

            {useExistingCustomer ? (
              <div className="space-y-4">
                <FormField label="Select Customer" required>
                  <select value={selectedCustomerId} onChange={e => setSelectedCustomerId(e.target.value)}
                    className={`w-full rounded-md border p-2.5 ${inputCls}`} data-testid="existing-customer-select">
                    <option value="">Choose customer...</option>
                    {existingCustomers.map(c => <option key={c.id} value={c.id}>{c.name} ({c.type}{c.city ? ` - ${c.city}` : ''})</option>)}
                  </select>
                </FormField>
                <Button onClick={() => setStep(3)} disabled={!selectedCustomerId}
                  className="bg-teal-600 hover:bg-teal-700 w-full h-11">
                  Next: Assign Operator <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            ) : createdCustomer ? (
              <div className="space-y-4">
                <div className="bg-emerald-50 dark:bg-green-900/20 border border-emerald-200 dark:border-green-700 rounded-lg p-4 text-center">
                  <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
                  <p className="text-green-700 dark:text-green-400 font-semibold">Customer Registered</p>
                  <p className="text-slate-900 dark:text-white">{createdCustomer.name} ({createdCustomer.code})</p>
                </div>
                <Button onClick={() => setStep(3)} className="bg-teal-600 hover:bg-teal-700 w-full h-11">
                  Next: Assign Operator <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="Customer Name" required>
                    <Input value={custForm.name} onChange={e => setCustForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="e.g. Acme Hospital" className={inputCls} data-testid="customer-name-input" />
                  </FormField>
                  <FormField label="Customer Type">
                    <select value={custForm.customer_type}
                      onChange={e => setCustForm(f => ({ ...f, customer_type: e.target.value }))}
                      className={`w-full rounded-md border p-2.5 ${inputCls}`}>
                      <option value="STORE">Store / Pharmacy / Clinic</option>
                      <option value="HUB">Hub / Cold Room</option>
                      <option value="WAREHOUSE">Warehouse</option>
                    </select>
                  </FormField>
                  <FormField label="Address">
                    <Input value={custForm.address} onChange={e => setCustForm(f => ({ ...f, address: e.target.value }))}
                      placeholder="Street, Locality" className={inputCls} />
                  </FormField>
                  <FormField label="City">
                    <Input value={custForm.city} onChange={e => setCustForm(f => ({ ...f, city: e.target.value }))}
                      placeholder="Delhi" className={inputCls} />
                  </FormField>
                  <FormField label="State">
                    <Input value={custForm.state} onChange={e => setCustForm(f => ({ ...f, state: e.target.value }))}
                      placeholder="Delhi" className={inputCls} />
                  </FormField>
                  <FormField label="Pin Code">
                    <Input value={custForm.pin_code} onChange={e => setCustForm(f => ({ ...f, pin_code: e.target.value }))}
                      placeholder="110001" className={inputCls} />
                  </FormField>
                  <FormField label="Contact Phone">
                    <Input value={custForm.contact_phone} onChange={e => setCustForm(f => ({ ...f, contact_phone: e.target.value }))}
                      placeholder="+91-XXXXXXXXXX" className={inputCls} />
                  </FormField>
                  <FormField label="Contact Email">
                    <Input value={custForm.contact_email} onChange={e => setCustForm(f => ({ ...f, contact_email: e.target.value }))}
                      placeholder="contact@customer.com" className={inputCls} />
                  </FormField>
                </div>
                <Button onClick={handleRegisterCustomer} disabled={loading}
                  className="bg-teal-600 hover:bg-teal-700 w-full h-11" data-testid="register-customer-btn">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Building2 className="w-4 h-4 mr-2" />}
                  Register Customer
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 3: Assign Operator */}
      {step === 3 && (
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="text-slate-900 dark:text-white flex items-center gap-2">
              <UserCog className="w-5 h-5 text-purple-500" />Step 3 — Assign Device to Driver/Operator
            </CardTitle>
          </CardHeader>
          <CardContent>
            {assignResult ? (
              <div className="space-y-4">
                <div className="bg-emerald-50 dark:bg-green-900/20 border border-emerald-200 dark:border-green-700 rounded-lg p-4 text-center">
                  <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
                  <p className="text-green-700 dark:text-green-400 font-semibold">{assignResult.message}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { setAssignResult(null); setSelectedDeviceRid(''); setSelectedOperatorId(''); }}
                    className="border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300">
                    <Plus className="w-4 h-4 mr-1" />Assign Another
                  </Button>
                  <Button onClick={() => setStep(4)} className="bg-teal-600 hover:bg-teal-700 flex-1">
                    Next: Activate Passports <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <FormField label="Device (IN_STOCK)" required>
                  <select value={selectedDeviceRid} onChange={e => setSelectedDeviceRid(e.target.value)}
                    className={`w-full rounded-md border p-2.5 ${inputCls}`} data-testid="device-rid-select">
                    <option value="">Choose device...</option>
                    {inventoryDevices.map(d => (
                      <option key={d.registry_id} value={d.registry_id}>
                        {d.device_uid} — IMEI: {d.imei} ({d.device_type})
                      </option>
                    ))}
                  </select>
                  {inventoryDevices.length === 0 && (
                    <p className="text-amber-600 dark:text-amber-400 text-xs mt-1">No in-stock devices. Receive devices in Step 1 first.</p>
                  )}
                </FormField>
                <FormField label="Operator / Driver / Hub Manager" required>
                  <select value={selectedOperatorId} onChange={e => setSelectedOperatorId(e.target.value)}
                    className={`w-full rounded-md border p-2.5 ${inputCls}`} data-testid="operator-select">
                    <option value="">Choose operator...</option>
                    {drivers.map(d => <option key={d.id} value={d.id}>{d.name} ({d.email})</option>)}
                  </select>
                </FormField>
                <Button onClick={handleAssignOperator} disabled={loading || !selectedDeviceRid || !selectedOperatorId}
                  className="bg-purple-600 hover:bg-purple-700 w-full h-11" data-testid="assign-operator-btn">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserCog className="w-4 h-4 mr-2" />}
                  Assign Device to Operator
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 4: Activate Passports */}
      {step === 4 && (
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="text-slate-900 dark:text-white flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-amber-500" />Step 4 — Activate Passport QR Labels
            </CardTitle>
          </CardHeader>
          <CardContent>
            {activatedPassports.length > 0 ? (
              <div className="space-y-4">
                <div className="bg-emerald-50 dark:bg-green-900/20 border border-emerald-200 dark:border-green-700 rounded-lg p-4 text-center">
                  <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
                  <p className="text-green-700 dark:text-green-400 font-semibold">{activatedPassports.length} Passport QR(s) Activated</p>
                </div>
                <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4">
                  <p className="text-slate-500 dark:text-slate-500 text-xs mb-2 uppercase tracking-wider">Activated Labels:</p>
                  <div className="flex flex-wrap gap-2">
                    {activatedPassports.slice(0, 6).map(p => (
                      <span key={p.passport_id} className="font-mono text-xs text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/20 px-2 py-1 rounded">
                        {p.label_uid}
                      </span>
                    ))}
                    {activatedPassports.length > 6 && (
                      <span className="text-slate-500 dark:text-slate-500 text-xs px-2 py-1">+{activatedPassports.length - 6} more</span>
                    )}
                  </div>
                </div>
                <Button onClick={() => setStep(5)} className="bg-teal-600 hover:bg-teal-700 w-full h-11">
                  Next: Dispatch Shipment <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <FormField label="Passport QR Book" required>
                  <select value={selectedBatchId} onChange={e => setSelectedBatchId(e.target.value)}
                    className={`w-full rounded-md border p-2.5 ${inputCls}`} data-testid="passport-batch-select">
                    <option value="">Choose book...</option>
                    {batches.map(b => (
                      <option key={b.id} value={b.id}>
                        {b.batch_number} — {b.dormant_count} dormant of {b.total_labels}
                      </option>
                    ))}
                  </select>
                  {batches.length === 0 && (
                    <p className="text-amber-400 text-xs mt-1">No Passport QR books yet. Track0tic must dispatch one to your tenant first.</p>
                  )}
                </FormField>
                <div className="grid grid-cols-2 gap-4">
                  <FormField label="Quantity" required>
                    <Input type="number" min={1} max={500} value={activateForm.quantity}
                      onChange={e => setActivateForm(f => ({ ...f, quantity: e.target.value }))}
                      className={inputCls} data-testid="quantity-input" />
                  </FormField>
                  <FormField label="Invoice Number">
                    <Input value={activateForm.invoice_number}
                      onChange={e => setActivateForm(f => ({ ...f, invoice_number: e.target.value }))}
                      placeholder="INV-9001" className={inputCls} />
                  </FormField>
                </div>
                <FormField label="Product Description">
                  <Input value={activateForm.product_description}
                    onChange={e => setActivateForm(f => ({ ...f, product_description: e.target.value }))}
                    placeholder="e.g. Insulin batch INS-001 — 2°C to 8°C" className={inputCls} />
                </FormField>
                <FormField label="Customer Name (printed on cert)">
                  <Input value={activateForm.customer_name || customerName()}
                    onChange={e => setActivateForm(f => ({ ...f, customer_name: e.target.value }))}
                    className={inputCls} />
                </FormField>
                <Button onClick={handleActivatePassports} disabled={loading || !selectedBatchId}
                  className="bg-amber-600 hover:bg-amber-700 w-full h-11" data-testid="activate-passports-btn">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                  Activate {activateForm.quantity} Passport QR{activateForm.quantity > 1 ? 's' : ''}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 5: Dispatch */}
      {step === 5 && (
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="text-slate-900 dark:text-white flex items-center gap-2">
              <Truck className="w-5 h-5 text-green-500" />Step 5 — Dispatch Shipment to {customerName()}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dispatchResult ? (
              <div className="space-y-4">
                <div className="bg-emerald-50 dark:bg-green-900/20 border border-emerald-200 dark:border-green-700 rounded-lg p-6 text-center">
                  <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-3" />
                  <p className="text-green-400 font-bold text-lg">Shipment Dispatched</p>
                  <p className="text-white">{dispatchResult.message}</p>
                </div>
                <div className="bg-slate-900 rounded-lg p-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-slate-400">Work Order</span><span className="font-mono text-white">{dispatchResult.work_order_number}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Customer</span><span className="text-white">{dispatchResult.customer_name}</span></div>
                  {dispatchResult.device_uid && <div className="flex justify-between"><span className="text-slate-400">Device</span><span className="font-mono text-purple-400">{dispatchResult.device_uid}</span></div>}
                  <div className="flex justify-between"><span className="text-slate-400">Passports</span><span className="text-amber-400">{dispatchResult.passports_linked} linked</span></div>
                </div>
                <Button onClick={() => {
                  setStep(1); setReceiveResult(null); setCreatedCustomer(null); setAssignResult(null);
                  setActivatedPassports([]); setDispatchResult(null); setSelectedInbound([]);
                  setSelectedDeviceRid(''); setSelectedOperatorId(''); setSelectedBatchId('');
                  setUseExistingCustomer(false); setSelectedCustomerId('');
                  fetchAll();
                }} className="bg-blue-600 hover:bg-blue-700 w-full h-11" data-testid="start-new-shipment-btn">
                  <Package className="w-4 h-4 mr-2" />Start New Onboarding
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 space-y-2 text-sm">
                  <p className="text-slate-500 dark:text-slate-500 text-xs uppercase tracking-wider mb-2">Shipment Summary</p>
                  <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Customer</span><span className="text-slate-900 dark:text-white">{customerName()}</span></div>
                  {assignResult && <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Device</span><span className="font-mono text-purple-600 dark:text-purple-400">{assignResult.device_uid}</span></div>}
                  {assignResult && <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Operator</span><span className="text-slate-900 dark:text-white">{assignResult.operator_name}</span></div>}
                  {activatedPassports.length > 0 && <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Passports</span><span className="text-amber-700 dark:text-amber-400">{activatedPassports.length}</span></div>}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <FormField label="Temp Min (°C)">
                    <Input type="number" step="0.5" value={shipForm.temp_min}
                      onChange={e => setShipForm(f => ({ ...f, temp_min: e.target.value }))}
                      className={inputCls} />
                  </FormField>
                  <FormField label="Temp Max (°C)">
                    <Input type="number" step="0.5" value={shipForm.temp_max}
                      onChange={e => setShipForm(f => ({ ...f, temp_max: e.target.value }))}
                      className={inputCls} />
                  </FormField>
                </div>
                <FormField label="Notes">
                  <Input value={shipForm.notes} onChange={e => setShipForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Special handling instructions" className={inputCls} />
                </FormField>
                <Button onClick={handleDispatch} disabled={loading}
                  className="bg-green-600 hover:bg-green-700 w-full h-11" data-testid="dispatch-shipment-btn">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Truck className="w-4 h-4 mr-2" />}
                  Dispatch Now
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {step > 1 && !dispatchResult && (
        <Button variant="ghost" onClick={() => setStep(step - 1)} className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white">
          <ChevronLeft className="w-4 h-4 mr-1" />Back to Step {step - 1}
        </Button>
      )}
    </div>
  );
}
